
import { defineComponent, PropType } from 'vue';
import { downloadFileAsBlob } from '@/api/attachedFile';

declare global {
  interface Window {
    pdfjsLib: any
  }
}

// PDF.js CDN 동적 로딩
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Script load error: ${src}`));
    document.head.appendChild(script);
  });
}

type LayoutType = 'vertical' | 'horizontal' | 'grid' | 'slide';

export default defineComponent({
  name: 'PdfViewer',

  props: {
    fileUid: { type: String, required: true },
    layout: {
      type: String as PropType<LayoutType>,
      default: 'vertical',
      validator: (value: string) => ['vertical', 'horizontal', 'grid', 'slide'].includes(value),
    },
    scale: { type: Number, default: 1.0 }, // Tip: 속도를 위해 1.0~1.2 정도로 낮추는 것도 방법입니다.
    showPageNumber: { type: Boolean, default: true },
    autoLoad: { type: Boolean, default: true },
  },

  data() {
    return {
      pdfDoc: null as any, // PDF 문서 객체 저장
      images: [] as (string | null)[], // 변환된 이미지 (아직 안된건 null)
      loading: false,
      error: '',
      pdfLoaded: false,
      currentSlideIndex: 0,
      totalPageCount: 0,
    };
  },

  computed: {
    layoutClass(): string {
      return `layout-${this.layout}`;
    },
    // currentSlideImage는 이제 images 배열을 직접 참조하므로 삭제해도 됩니다.
    isFirstSlide(): boolean {
      return this.currentSlideIndex === 0;
    },
    isLastSlide(): boolean {
      return this.totalPageCount > 0 && this.currentSlideIndex === this.totalPageCount - 1;
    },
  },

  watch: {
    fileUid: {
      handler(newVal) {
        if (newVal && this.autoLoad && this.pdfLoaded) {
          this.loadPdfFile();
        }
      },
    },
    // 슬라이드 페이지가 바뀔 때마다 해당 페이지 렌더링 시도
    currentSlideIndex(newIndex) {
      if (this.layout === 'slide' && this.pdfDoc) {
        this.renderPageAndNeighbors(newIndex);
      }
    },
  },

  async mounted() {
    try {
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js');
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
      }
      this.pdfLoaded = true;
      if (this.fileUid && this.autoLoad) {
        this.loadPdfFile();
      }
    } catch (error) {
      console.error('PDF.js 로드 실패:', error);
      this.error = 'PDF 라이브러리 로드에 실패했습니다.';
    }
  },

  methods: {
    blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result instanceof ArrayBuffer) resolve(reader.result);
          else reject(new Error('FileReader result is not an ArrayBuffer'));
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
      });
    },

    async loadPdfFile() {
      if (!this.pdfLoaded) { /* ... 기존 대기 로직 ... */ }
      if (!this.fileUid) return;

      this.loading = true;
      this.pdfDoc = null;
      this.images = [];
      this.error = '';
      this.currentSlideIndex = 0;

      try {
        const res = await downloadFileAsBlob(this.fileUid);
        let blob: Blob;
        // ... Blob 처리 로직 (기존과 동일) ...
        if (res.data instanceof Blob) blob = res.data;
        else if (res.data instanceof ArrayBuffer) blob = new Blob([res.data], { type: 'application/pdf' });
        else blob = new Blob([res.data], { type: 'application/pdf' }); // Fallback

        // 1. 문서를 엽니다. (여기서는 페이지만 가져옴, 렌더링 X)
        const arrayBuffer = await this.blobToArrayBuffer(blob);
        this.pdfDoc = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        this.totalPageCount = this.pdfDoc.numPages;

        // 2. 페이지 수만큼 빈 배열 생성 (null로 채움)
        this.images = new Array(this.totalPageCount).fill(null);

        // 3. 초기 렌더링 시작
        if (this.layout === 'slide') {
          // 슬라이드는 현재 페이지만 우선 렌더링
          await this.renderPageAndNeighbors(0);
        } else {
          // 세로 모드는 1페이지부터 순차적으로 렌더링 (사용자에게 바로 보여줌)
          this.renderAllPagesSequentially();
        }

        this.$emit('loaded', this.totalPageCount);
      } catch (error) {
        console.error('PDF 불러오기 오류:', error);
        this.error = 'PDF 불러오기 중 오류가 발생했습니다.';
      } finally {
        this.loading = false;
      }
    },

    // [최적화 핵심] 특정 페이지만 렌더링하는 함수
    async renderPage(pageIndex: number) {
      // 범위 체크 및 이미 렌더링된 페이지는 스킵
      if (pageIndex < 0 || pageIndex >= this.totalPageCount) return;
      if (this.images[pageIndex]) return;

      try {
        const page = await this.pdfDoc.getPage(pageIndex + 1); // PDF 페이지는 1부터 시작
        const viewport = page.getViewport({ scale: this.scale });
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) return;

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({ canvasContext: ctx, viewport }).promise;

        // Vue 반응성을 위해 배열 업데이트 (Vue 2: this.$set, Vue 3: 직접 할당)
        this.images.splice(pageIndex, 1, canvas.toDataURL('image/png'));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`${pageIndex + 1} 페이지 렌더링 실패`, error);
      }
    },

    // 슬라이드 모드용: 현재 페이지 + 다음 페이지(프리로딩)
    async renderPageAndNeighbors(index: number) {
      await this.renderPage(index); // 현재 페이지 (가장 중요)
      this.renderPage(index + 1); // 다음 페이지 (미리 로드)
      // this.renderPage(index - 1); // 필요하면 이전 페이지도
    },

    // 일반 모드용: 1페이지부터 차례대로 렌더링
    async renderAllPagesSequentially() {
      // [수정 1] i++ 대신 i += 1 사용 (no-plusplus 해결)
      for (let i = 0; i < this.totalPageCount; i += 1) {
        if (!this.pdfDoc) break;

        // [수정 2] 의도적인 순차 처리를 위해 ESLint 규칙을 이 줄만 무시 (no-await-in-loop 해결)
        // eslint-disable-next-line no-await-in-loop
        await this.renderPage(i);

        // (선택사항) 렌더링 사이사이에 브라우저 숨통을 틔워줌
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 10));
      }
    },

    reload() { this.loadPdfFile(); },

    nextSlide() {
      if (this.currentSlideIndex < this.totalPageCount - 1) {
        this.currentSlideIndex += 1;
      }
    },
    prevSlide() {
      if (this.currentSlideIndex > 0) {
        this.currentSlideIndex -= 1;
      }
    },
    goToSlide(index: number) {
      if (index >= 0 && index < this.totalPageCount) {
        this.currentSlideIndex = index;
      }
    },
  },
});
